import {
  Box,
  Checkbox,
  Flex,
  Spinner,
  Text,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { getAdvancedMetricGraphData } from "../../../api/expenseBreakdown";
import { customCurrencyFormat } from "../../../utils";

const lineConfig = [
  { key: "Profit", color: "#38A169" },
  { key: "Income", color: "#00B5D8" },
  { key: "Expenses", color: "#E53E3E" },
  { key: "Team", color: "#ED8936" },
  { key: "Marketing", color: "#627CFB" },
  { key: "Operations", color: "#38B2AC" },
  { key: "Products", color: "#ED64A6" },
];

const typeToLineKey = {
  income: "Income",
  profit: "Profit",
  expense: "Expenses",
  team: "Team",
  marketing: "Marketing",
  overhead: "Operations",
  cogs: "Products",
};

const AdvancedMetricsGraph = ({
  selectedTag,
  defaultCheck,
  customerId,
  formattedDate,
}) => {
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const resolveDefaultKey = useCallback(() => {
    if (!defaultCheck) return null;
    const lower = String(defaultCheck).toLowerCase();
    return (
      typeToLineKey[lower] ||
      lineConfig.find((l) => l.key.toLowerCase() === lower)?.key ||
      null
    );
  }, [defaultCheck]);

  const buildVisibility = useCallback(() => {
    const activeKey = resolveDefaultKey();
    return lineConfig.reduce((acc, line) => {
      acc[line.key] = line.key === activeKey;
      return acc;
    }, {});
  }, [resolveDefaultKey]);

  const [visibleLines, setVisibleLines] = useState(buildVisibility);

  const adjusted = JSON.parse(
    sessionStorage.getItem("adjustedView") || "false"
  );

  useEffect(() => {
    if (selectedTag) {
      const key = typeToLineKey[selectedTag.toLowerCase()];
      if (key) {
        setVisibleLines(
          lineConfig.reduce((acc, line) => {
            acc[line.key] = line.key === key;
            return acc;
          }, {})
        );
      }
    } else {
      setVisibleLines((prev) => ({ ...prev }));
    }
  }, [selectedTag]);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    if (!customerId || !formattedDate) return;

    const fetchAllMetrics = async () => {
      try {
        setLoading(true);
        setError(null);

        const requests = Object.keys(typeToLineKey).map((type) =>
          getAdvancedMetricGraphData(
            customerId,
            formattedDate,
            type,
            controller.signal,
            { adjusted } // <-- pass adjusted here
          )
            .then((res) => ({ type, data: res.data }))
            .catch(() => ({ type, data: null }))
        );

        const results = await Promise.all(requests);
        if (!isMounted) return;

        let months = [];
        results.forEach((r) => {
          if (r.data?.months) {
            months = r.data.months;
          }
        });

        const merged = months.map((month, idx) => {
          const entry = { month };
          results.forEach((r) => {
            const key = typeToLineKey[r.type];
            entry[key] = r.data?.values[idx] || 0;
          });
          return entry;
        });

        setAllData(merged);
      } catch (err) {
        if (!controller.signal.aborted) {
          setError("Failed to fetch metric graph data");
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchAllMetrics();
    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [customerId, formattedDate, adjusted]);

  useEffect(() => {
    setVisibleLines(buildVisibility());
  }, [buildVisibility]);

  const toggleLine = (key) => {
    setVisibleLines((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" minH="300px">
        <Spinner size="lg" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex justify="center" align="center" minH="300px">
        <Text color="red.500">{error}</Text>
      </Flex>
    );
  }

  return (
    <Box
      width="100%"
      bg="white"
      p={4}
      borderRadius="12px"
      minH={"396px"}
      sx={{
        "& svg:focus, g:focus, path:focus": { outline: "none !important" },
      }}
    >
      <Flex
        direction="row"
        align="center"
        justify="space-between"
        flexWrap="wrap"
        mt="5px"
        mb="5px"
        px={4}
      >
        <Text fontSize="20px" fontWeight="700" mb={{ base: 2, md: 0 }}>
          12-Month Trend Overview
        </Text>
        <Wrap spacing={3}>
          {lineConfig.map((line) => {
            const isActive = !!visibleLines[line.key];
            return (
              <WrapItem key={line.key}>
                <Checkbox
                  isChecked={isActive}
                  onChange={() => toggleLine(line.key)}
                  size="lg"
                  spacing={0}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  borderRadius="6px"
                  px={2}
                  py={1}
                  minW="80px"
                  bg={isActive ? line.color : "white"}
                  color={isActive ? "white" : "gray.700"}
                  border={`1px solid ${isActive ? line.color : "#E5E8F0"}`}
                  _hover={{ bg: isActive ? line.color : "gray.100" }}
                  _checked={{
                    bg: line.color,
                    color: "white",
                    borderColor: line.color,
                  }}
                  _focus={{ boxShadow: "none", outline: "none" }}
                  sx={{
                    ".chakra-checkbox__control": {
                      display: "none",
                      boxShadow: "none",
                    },
                    ".chakra-checkbox__label": {
                      marginInlineStart: "0 !important",
                      width: "100%",
                      display: "block",
                      textAlign: "center",
                      fontWeight: 600,
                      fontSize: "14px",
                    },

                    ".chakra-checkbox__control[data-focus]": {
                      boxShadow: "none",
                    },
                    ".chakra-checkbox__control[data-focus-visible]": {
                      boxShadow: "none",
                    },
                  }}
                >
                  {line.key}
                </Checkbox>
              </WrapItem>
            );
          })}
        </Wrap>
      </Flex>

      <ResponsiveContainer width="100%" height={300} debounce={500}>
        <LineChart
          data={allData}
          margin={{ top: 20, right: 20, left: 20, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#CCCCCC" />
          <XAxis
            dataKey="month"
            tick={{ fill: "#6b7280", fontSize: 16, dy: 10 }}
            tickLine={false}
          />
          <YAxis
            tickFormatter={(value) =>
              `${customCurrencyFormat(value, false, false, true, false)}`
            }
            tick={{ fill: "#6b7280", fontSize: 16 }}
            domain={[0, "auto"]}
          />
          <Tooltip
            formatter={(value) =>
              `${customCurrencyFormat(value, false, false, true, false)}`
            }
            labelFormatter={() => ""}
            contentStyle={{
              borderRadius: "8px",
              border: "1px solid #E5E7EB",
              backgroundColor: "#fff",
              color: "#111827",
            }}
          />

          {lineConfig.map(
            (line) =>
              visibleLines[line.key] && (
                <Line
                  key={line.key}
                  type="monotone"
                  dataKey={line.key}
                  stroke={line.color}
                  strokeWidth={8}
                  dot={{
                    r: 6,
                    fill: "#ffffff",
                    stroke: line.color,
                    strokeWidth: 1,
                  }}
                  activeDot={{
                    r: 6,
                    fill: "#ffffff",
                    stroke: line.color,
                    strokeWidth: 1,
                  }}
                />
              )
          )}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default AdvancedMetricsGraph;
